import React from "react";
import { Box, Grid, Stack, Typography, Paper, Chip, Skeleton } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SarIcon from "../../icons/SarIcon";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const cardSx = {
  p: 3,
  borderRadius: 1.5,
  boxShadow: (theme) => theme.palette.shadow.statCard,
  border: (theme) => `1px solid ${theme.palette.border.light}`,
  bgcolor: "background.paper",
  height: "100%",
};

function formatSar(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `SAR ${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `SAR ${(x / 1_000).toFixed(1)}K`;
  return `SAR ${new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(x)}`;
}

const StatCard = ({ label, value, chip, icon, isLoading }) => (
  <Paper elevation={0} sx={cardSx}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {React.cloneElement(icon, {
          sx: { color: (theme) => theme.palette.statIcon.users.color, fontSize: 22 },
        })}
      </Box>
      {chip && (
        <Chip
          label={chip}
          size="small"
          sx={{
            bgcolor: (theme) => theme.palette.chips.success.bg,
            color: (theme) => theme.palette.chips.success.text,
            fontWeight: 600,
            fontSize: 11,
            height: 22,
            borderRadius: 999,
          }}
        />
      )}
    </Stack>
    <Typography variant="body2" sx={{ color: "text.muted", mt: 2 }}>
      {label}
    </Typography>
    {isLoading ? (
      <Skeleton variant="text" width="60%" height={40} />
    ) : (
      <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary", mt: 0.5, letterSpacing: -0.5 }}>
        {value}
      </Typography>
    )}
  </Paper>
);

export default function InvestorDashboardStats({ summary = {}, isLoading = false }) {
  const cards = [
    {
      key: "portfolio",
      label: "Total Portfolio",
      value: formatSar(summary.totalPortfolio),
      chip: null,
      icon: <AccountBalanceWalletOutlinedIcon />,
    },
    {
      key: "income",
      label: "Monthly Income",
      value: formatSar(summary.monthlyIncome),
      chip: null,
      icon: <SarIcon />,
    },
    {
      key: "investment",
      label: "Total Investment",
      value: formatSar(summary.totalInvestment),
      chip: summary.totalAssets != null ? `${summary.totalAssets} Assets` : null,
      icon: <ApartmentOutlinedIcon />,
    },
    {
      key: "roi",
      label: "Average ROI",
      value: summary.averageROI != null ? `${summary.averageROI}%` : "—",
      chip: summary.roiLabel || null,
      icon: <TrendingUpIcon />,
    },
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {cards.map((card) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.key}>
          <StatCard {...card} isLoading={isLoading} />
        </Grid>
      ))}
    </Grid>
  );
}
